import { useState } from 'react';
import { api } from '../../api/client.js';
import {
  Alert,
  Field,
  Notice,
  PageHeader,
  datum,
  fehlertext,
  useLoad,
} from '../shared/ui.js';

/**
 * G-ST-P/OE, ADR-0022: Ring- und Profilpreispflege -- nur Betreiber.
 *
 * Dasselbe Grundmuster wie die Preislistenpflege (`PriceListsPage`): das Raster
 * wird immer vollstaendig als neuer Entwurf gespeichert, nie in-place geaendert;
 * ein Entwurf wird erst nach ausdruecklicher Bestaetigung aktiviert.
 */

interface Version {
  id: string;
  versionLabel: string;
  status: 'draft' | 'active' | 'archived';
  isSynthetic: boolean;
  note: string | null;
  createdAt: string;
  activatedAt: string | null;
  ringRowCount: number;
  profileRowCount: number;
  usedByQuotes: number;
}

interface RingRow {
  ringMaterial: 'STEEL_1_4301' | 'STEEL_1_4571';
  geometryClass: string;
  sheetThicknessMm: string;
  quantityTier: 'TIER_1_4' | 'TIER_5_PLUS';
  listPricePerUnit: string | null;
}
interface ProfileRow {
  profileArticleCode: string;
  elastomerCode: string;
  profileWidthMm: number;
  requiredSheetThicknessMm: number;
  listPricePerMeter: string | null;
}
interface Grid {
  basedOnVersionId: string | null;
  basedOnVersionLabel: string | null;
  ringRows: RingRow[];
  profileRows: ProfileRow[];
}

const KLASSEN_LABEL: Record<string, string> = {
  CLASS_200_400: '200–400 mm',
  CLASS_400_600: '>400–600 mm',
  CLASS_600_800: '>600–800 mm',
  CLASS_800_1000: '>800–1.000 mm',
  CLASS_1000_1300: '>1.000–1.300 mm',
};
const STAFFEL_LABEL: Record<string, string> = {
  TIER_1_4: '1 bis 4',
  TIER_5_PLUS: 'ab 5',
};
const ELASTOMER_LABEL: Record<string, string> = {
  '04': 'EPDM',
  '13': 'NBR',
  '08': 'FKM',
};

export function OePricingPage(): React.JSX.Element {
  const versions = useLoad<{ items: Version[] }>('/api/admin/oe-pricing/versions');
  const grid = useLoad<Grid>('/api/admin/oe-pricing/grid');
  const [err, setErr] = useState<string | null>(null);

  async function aktivieren(v: Version): Promise<void> {
    if (
      !window.confirm(
        `Version ${v.versionLabel} aktivieren? Die bisher aktive Version wird archiviert. ` +
          'Neue OE-Angebote rechnen ab sofort mit diesem Raster.',
      )
    )
      return;
    setErr(null);
    try {
      await api.post(`/api/admin/oe-pricing/versions/${v.id}/activate`);
      versions.reload();
      grid.reload();
    } catch (e) {
      setErr(fehlertext(e));
    }
  }

  return (
    <div>
      <PageHeader
        title="G-ST-P/OE Preispflege"
        lead="Ring- und Profillistenpreise der konfigurierten Stuetzringdichtung. Beide Preise durchlaufen dieselbe Rabattfindung wie ein Katalogartikel -- hier steht ausschliesslich der Listenpreis."
      />
      <Alert text={err} />

      <section className="card">
        <h3>Versionen</h3>
        {versions.loading && <p>Laedt…</p>}
        <Alert text={versions.error} />
        {versions.data !== null && (
          <table>
            <thead>
              <tr>
                <th>Bezeichnung</th>
                <th>Status</th>
                <th>Raster</th>
                <th>Angelegt</th>
                <th>Aktiviert</th>
                <th>Verwendet von</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {versions.data.items.map((v) => (
                <tr key={v.id}>
                  <td>
                    {v.versionLabel}
                    {v.isSynthetic && (
                      <span
                        className="pill pill-off"
                        title="Platzhalterpreise, nicht freigegeben"
                      >
                        {' '}
                        synthetisch
                      </span>
                    )}
                  </td>
                  <td>{v.status}</td>
                  <td>
                    {v.ringRowCount}/40 Ring, {v.profileRowCount}/12 Profil
                  </td>
                  <td>{datum(v.createdAt)}</td>
                  <td>{datum(v.activatedAt)}</td>
                  <td>{v.usedByQuotes} Angebot(e)</td>
                  <td>
                    {v.status === 'draft' && (
                      <button type="button" onClick={() => void aktivieren(v)}>
                        Aktivieren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {grid.loading && <p>Laedt…</p>}
      <Alert text={grid.error} />
      {grid.data !== null && (
        <RasterFormular
          grid={grid.data}
          onDone={() => {
            versions.reload();
            grid.reload();
          }}
        />
      )}
    </div>
  );
}

function RasterFormular({
  grid,
  onDone,
}: {
  grid: Grid;
  onDone: () => void;
}): React.JSX.Element {
  const [ring, setRing] = useState(
    grid.ringRows.map((r) => ({ ...r, listPricePerUnit: r.listPricePerUnit ?? '' })),
  );
  const [profil, setProfil] = useState(
    grid.profileRows.map((p) => ({ ...p, listPricePerMeter: p.listPricePerMeter ?? '' })),
  );
  const [versionLabel, setVersionLabel] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function speichern(): Promise<void> {
    setErr(null);
    setBusy(true);
    try {
      const res = await api.post<{ id: string }>('/api/admin/oe-pricing/versions', {
        versionLabel,
        ringPrices: ring.map((r) => ({
          ringMaterial: r.ringMaterial,
          geometryClass: r.geometryClass,
          sheetThicknessMm: r.sheetThicknessMm,
          quantityTier: r.quantityTier,
          listPricePerUnit: r.listPricePerUnit.replace(',', '.'),
        })),
        profilePrices: profil.map((p) => ({
          profileArticleCode: p.profileArticleCode,
          listPricePerMeter: p.listPricePerMeter.replace(',', '.'),
        })),
      });
      setVersionLabel('');
      window.alert(
        `Entwurf ${res.id} angelegt. Zum Wirksamwerden muss er oben ausdruecklich aktiviert werden.`,
      );
      onDone();
    } catch (e) {
      setErr(fehlertext(e));
    } finally {
      setBusy(false);
    }
  }

  const vollstaendig =
    ring.every((r) => r.listPricePerUnit.trim() !== '') &&
    profil.every((p) => p.listPricePerMeter.trim() !== '');

  return (
    <section className="card">
      <h3>Raster als neuen Entwurf speichern</h3>
      <p className="note">
        {grid.basedOnVersionLabel !== null
          ? `Vorbelegt aus „${grid.basedOnVersionLabel}“.`
          : 'Noch kein Raster vorhanden -- alle Felder sind leer.'}{' '}
        Ein Speichern erzeugt immer eine vollstaendig neue Entwurfsversion, nie eine
        Aenderung der bestehenden.
      </p>

      <h4>Stuetzring (40 Kombinationen, Preis je Stück)</h4>
      <table>
        <thead>
          <tr>
            <th>Ringwerkstoff</th>
            <th>Geometrieklasse</th>
            <th>Blechdicke</th>
            <th>Menge</th>
            <th>Preis je Stück</th>
          </tr>
        </thead>
        <tbody>
          {ring.map((r, i) => (
            <tr
              key={`${r.ringMaterial}-${r.geometryClass}-${r.sheetThicknessMm}-${r.quantityTier}`}
            >
              <td>{r.ringMaterial === 'STEEL_1_4301' ? '1.4301' : '1.4571'}</td>
              <td>{KLASSEN_LABEL[r.geometryClass] ?? r.geometryClass}</td>
              <td>{r.sheetThicknessMm} mm</td>
              <td>{STAFFEL_LABEL[r.quantityTier] ?? r.quantityTier} Stück</td>
              <td>
                <input
                  value={r.listPricePerUnit}
                  placeholder="z. B. 120,00"
                  onChange={(e) =>
                    setRing(
                      ring.map((x, j) =>
                        j === i ? { ...x, listPricePerUnit: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>Elastomerprofil (12 Kombinationen, Preis je Meter)</h4>
      <table>
        <thead>
          <tr>
            <th>Positionskennung</th>
            <th>Werkstoff</th>
            <th>Breite</th>
            <th>Verlangte Blechdicke</th>
            <th>Preis je Meter</th>
          </tr>
        </thead>
        <tbody>
          {profil.map((p, i) => (
            <tr key={p.profileArticleCode}>
              <td>{p.profileArticleCode}</td>
              <td>{ELASTOMER_LABEL[p.elastomerCode] ?? p.elastomerCode}</td>
              <td>{p.profileWidthMm} mm</td>
              <td>{p.requiredSheetThicknessMm} mm</td>
              <td>
                <input
                  value={p.listPricePerMeter}
                  placeholder="z. B. 60,00"
                  onChange={(e) =>
                    setProfil(
                      profil.map((x, j) =>
                        j === i ? { ...x, listPricePerMeter: e.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Field label="Versionsbezeichnung">
        <input
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          placeholder="z. B. Lieferantenangebot 2026-09"
        />
      </Field>
      {!vollstaendig && (
        <Notice>Alle 40 Ring- und 12 Profilpreise muessen ausgefuellt sein.</Notice>
      )}
      <Alert text={err} />
      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={!versionLabel || !vollstaendig || busy}
          onClick={() => void speichern()}
        >
          Entwurf erzeugen
        </button>
      </div>
    </section>
  );
}
